import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';

import { ToastService } from '../../../modules/shared/services/toast-message.service';
import { MyTasksService } from '../../../services/mytasks/mytasks.service';
import { PagedResponse } from '../general/models/general';
import { IMyTask } from './models/MyTasks';
import * as actions from './mytasks.actions';

@Injectable()
export class MyTasksEffects {
  private actions$ = inject(Actions);
  private service = inject(MyTasksService);
  private toastService = inject(ToastService);

  getMyTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getMyTasks),
      // mergeMap (not exhaustMap): the 4 columns load in parallel and each
      // column's own "load more" must not be dropped while another is in flight.
      mergeMap(({ status, sortOptions, searchTerm, filters, pageNumber }) =>
        this.service
          .getMyTasksAll({
            ...sortOptions,
            searchTerm,
            filters,
            pageNumber,
            pageSize: 25,
            status,
          })
          .pipe(
            map((pagedMyTasks: PagedResponse<IMyTask>) => {
              return actions.getMyTasksSuccess({ status, pagedMyTasks });
            }),
            catchError((error: any) => of(actions.getMyTasksFailure(error))),
          ),
      ),
    ),
  );

  getMyTaskById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getMyTaskById),
      exhaustMap((action) =>
        this.service.getMyTaskById(action.taskId).pipe(
          map((myTaskById: IMyTask) => {
            return actions.getMyTaskByIdSuccess({ myTaskById });
          }),
          catchError((error: any) => of(actions.getMyTaskByIdFailure(error))),
        ),
      ),
    ),
  );

  updateMyTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateMyTasks),
      exhaustMap((action) =>
        this.service.updateMyTask(action.taskId, action.newBody).pipe(
          map(() => {
            return actions.updateMyTasksSuccess({
              taskId: action.taskId,
              newBody: action.newBody,
              refresh: action.refresh,
            });
          }),
          catchError((error: any) => of(actions.updateMyTasksFailure(error))),
        ),
      ),
    ),
  );

  updateMyTasksSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.updateMyTasksSuccess),
        tap(() => {
          this.toastService.success('Task updated successfully.');
        }),
      ),
    { dispatch: false },
  );
}
